// Schema-aware table for `bookmarks-v1` registry files (DSR 1.2.0).
// Renders registry/bookmarks.yaml — small URL-with-minimal-text
// entries clipped from the browser via tootasko-clipper's
// "Save as DSR Bookmark" action and paste-appended into the repo
// file (see the clipper-tasko integration spec §10 for the flow).
//
// CTSK-269 — parity with the Data Sources tab: metric cards row,
// shared RegistryTableHeader (search + source/tag filters + captured
// date range + reset), tri-state sortable columns, and an Add-entry
// guidance popover (bookmarks are clipper-authored per CTSK-249, so
// the button explains the flow instead of opening a form — a
// functional add-modal is deferred to a follow-up ticket).
//
// Columns: Title (linked to url), URL (truncated + full on hover),
// Captured (date-only), Source (<producer>@<version>), Tags (chips),
// Note (truncated + full on hover), ID (CTSK-350: truncated + full on
// hover, click copies the full id — bookmarks-v1 has no separate
// `uuid` field; the required kebab-slug `id` IS the stable entry
// identifier), Actions (CTSK-351: per-row palette rendered from the
// tool manifest's `registryFiles[].rowActions[]` — column hides when
// no rowActions are declared, no dead column artifact). Read-only —
// the write path is deliberately the git paste-append round-trip,
// not this UI.

import React, { useMemo, useState } from 'react';
import yaml from 'js-yaml';
import { ExternalLink, Pencil, X } from 'lucide-react';
import { validateRegistryEntry, type RegistryFileEntry } from '@captain-tasko/shared';
import { WarningRow } from './WarningRow.js';
import { RegistryTableHeader } from './RegistryTableHeader.js';
import { SortableTh } from './SortableTh.js';
import { RegistryRowActionsPalette } from './RegistryRowActionsPalette.js';
import { BookmarkRowEditor } from './BookmarkRowEditor.js';
import { useTableSort, type SortState } from '../lib/sort.js';
import { patchRegistryEntry } from '../lib/api.js';
import { copyToClipboard } from '../lib/clipboard.js';
import { toast } from './Toast.js';

// ── Schema, mirrors registry/bookmarks.yaml (schema_version 1) ──────
interface Bookmark {
  id: string;
  url: string;
  title: string;
  /** ISO 8601 timestamp; js-yaml may resolve it to a Date object. */
  captured_at: string;
  /** `<producer>@<version>`, e.g. tootasko-clipper@0.1.31 */
  source: string;
  note?: string;
  tags?: string[];
  /** CTSK-364 — per-tool fields captured back from external tools via
   *  rowActions fetch-and-map (external_refs.hyper_scan.card_url
   *  drives the Card column below). */
  external_refs?: Record<string, Record<string, unknown>>;
}

/** CTSK-364 — write-path wiring. When present the table becomes
 *  mutable: an Edit affordance appears in the Actions column (inline
 *  BookmarkRowEditor, one open at a time) and fetch-and-map row
 *  actions gain persistence + refresh. Absent → the CTSK-269
 *  read-only table, byte-for-byte. */
export interface BookmarksMutationContext {
  toolId: string;
  filePath: string;
  /** Per-entry {captain_tasko_ref} builder — the parent owns the
   *  route shape (e.g. `${origin}/tools/data-sources-registry?file=…
   *  &entry=…`); the table just threads it into interpolation. */
  makeEntryRef?: (entryId: string) => string;
  /** Refetch the registry YAML (the table re-renders from persisted
   *  bytes — no optimistic state). */
  onRefetch: () => void;
}

interface BookmarksFile {
  schema_version?: number;
  bookmarks?: Bookmark[];
  [k: string]: unknown;
}

// ── Component ─────────────────────────────────────────────────────────

// Sort accessors (CTSK-267 primitives) — module-level so the
// useTableSort memo key is referentially stable. captured_at is an
// ISO-8601 string (normalized below), so lexicographic compare IS
// chronological compare — no Date parsing needed.
const BOOKMARK_SORT_ACCESSORS = {
  title:       (b: Bookmark) => b.title,
  url:         (b: Bookmark) => b.url,
  captured_at: (b: Bookmark) => b.captured_at,
  source:      (b: Bookmark) => b.source,
  id:          (b: Bookmark) => b.id,
};

// CTSK-350 — click-to-copy for the ID column. Full id lands in the
// clipboard (the cell shows a CSS-truncated view); toast confirms.
// CTSK-281/282: always route through the copyToClipboard wrapper —
// raw navigator.clipboard fails silently on http:// Vault origins.
function copyBookmarkId(id: string): void {
  void copyToClipboard(id).then((ok) => {
    if (ok) toast.success(`ID ${id} copied`);
    else toast.error('Clipboard write failed — select the ID manually');
  });
}

export function BookmarksTable({
  yamlText,
  rowActions,
  defaultSort,
  mutation,
}: {
  yamlText: string;
  /** CTSK-351 — per-row action palette declarations from the tool
   *  manifest's `registryFiles[].rowActions[]`. When absent/empty the
   *  Actions column hides entirely (no dead column artifact). Optional
   *  so callers that don't have a manifest handy (fixtures, tests)
   *  render the pre-CTSK-351 shape unchanged. */
  rowActions?: RegistryFileEntry['rowActions'];
  /** CTSK-364 (CTSK-361A fold) — manifest-declared initial sort for
   *  this file (registryFiles[].defaultSort). Applied on mount; a
   *  user column-click overrides. Unset / unknown column → the
   *  built-in default `captured_at DESC` (latest capture at top). */
  defaultSort?: { column: string; direction: 'asc' | 'desc' };
  /** CTSK-364 — write-path context; absent = read-only table. */
  mutation?: BookmarksMutationContext;
}) {
  const hasRowActions = Boolean(rowActions && rowActions.length > 0);
  const editable = Boolean(mutation);
  // Actions column shows for manifest rowActions AND/OR the Edit
  // affordance — either alone is enough.
  const hasActionsColumn = hasRowActions || editable;
  // CTSK-364 (CTSK-353 fold) — at most one row's editor open.
  const [editingId, setEditingId] = useState<string | null>(null);
  const { entries, parseError } = useMemo<{
    entries: Bookmark[];
    parseError: string | null;
  }>(() => {
    if (!yamlText) return { entries: [], parseError: null };
    try {
      const parsed = yaml.load(yamlText) as BookmarksFile | null;
      const list = ((parsed?.bookmarks ?? []) as Bookmark[]).map(normalizeBookmark);
      return { entries: list, parseError: null };
    } catch (err) {
      return { entries: [], parseError: (err as Error).message };
    }
  }, [yamlText]);

  // ── CTSK-269: search / filter / sort state ─────────────────────────
  const [search, setSearch] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterTag, setFilterTag] = useState('');
  // Date-only range over `captured_at` (the table renders date-only).
  // Compared on the YYYY-MM-DD prefix as strings — TZ-safe: no local-
  // time Date parsing on either side of the comparison.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Add-entry guidance popover (deferred functional add — CTSK-249:
  // bookmarks are clipper-authored).
  const [showAddHelp, setShowAddHelp] = useState(false);

  const sourceOptions = useMemo(
    () => [...new Set(entries.map((b) => b.source).filter(Boolean))].sort(),
    [entries],
  );
  // Single-select tag filter — multi-select is a CTSK-269 non-goal.
  const tagOptions = useMemo(
    () => [...new Set(entries.flatMap((b) => b.tags ?? []).filter(Boolean))].sort(),
    [entries],
  );

  const filtered = useMemo(() => {
    let rows = entries;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((b) => {
        const hay = [b.id, b.url, b.title, b.source, b.note ?? '', ...(b.tags ?? [])]
          .join(' ').toLowerCase();
        return hay.includes(q);
      });
    }
    if (filterSource) rows = rows.filter((b) => b.source === filterSource);
    if (filterTag) rows = rows.filter((b) => (b.tags ?? []).includes(filterTag));
    if (dateFrom || dateTo) {
      rows = rows.filter((b) => {
        const d = dateOnly(b.captured_at);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return false;
        if (dateFrom && d < dateFrom) return false;
        if (dateTo && d > dateTo) return false;
        return true;
      });
    }
    return rows;
  }, [entries, search, filterSource, filterTag, dateFrom, dateTo]);

  // CTSK-364 (CTSK-361A fold) — initial sort. The manifest-declared
  // defaultSort wins when its column names a real sort accessor;
  // otherwise (unset / typo'd column) the built-in default is
  // `captured_at DESC` — "latest bookmark at top", replacing the
  // pre-CTSK-361 natural-file-order default. User column-clicks
  // override (useTableSort owns the live state; `initial` is
  // mount-only by design).
  const initialSort: SortState = useMemo(() => {
    if (defaultSort && defaultSort.column in BOOKMARK_SORT_ACCESSORS) {
      return { key: defaultSort.column, dir: defaultSort.direction };
    }
    return { key: 'captured_at', dir: 'desc' };
  }, [defaultSort]);
  const { sorted, sort, setSort } = useTableSort(filtered, BOOKMARK_SORT_ACCESSORS, initialSort);

  const reset = () => {
    setSearch('');
    setFilterSource('');
    setFilterTag('');
    setDateFrom('');
    setDateTo('');
    // Reset restores the on-mount state — the resolved default sort,
    // not the natural file order (CTSK-361A: the default IS a sort).
    setSort(initialSort);
  };

  // Metric cards — computed over the FULL entry list (valid + warning
  // rows), NOT the filtered view. Deliberate: mirrors the Data Sources
  // tab, whose KPI cards are also filter-independent.
  const metrics = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const recent = entries.filter((b) => {
      const t = Date.parse(b.captured_at);
      return !Number.isNaN(t) && t >= cutoff;
    }).length;
    const uniqueSources = new Set(entries.map((b) => b.source).filter(Boolean)).size;
    return { total: entries.length, recent, uniqueSources };
  }, [entries]);

  // CTSK-364 — Card column shows only when at least one row carries a
  // captured hyper-scanner card link (no dead column for registries
  // that have never been scanned; same discipline as the Actions
  // column gate).
  const hasCardLinks = useMemo(
    () => entries.some((b) => typeof b.external_refs?.hyper_scan?.card_url === 'string'
      && (b.external_refs.hyper_scan.card_url as string).length > 0),
    [entries],
  );

  // Total column count — the editor expansion row spans them all.
  const columnCount = 7 + (hasCardLinks ? 1 : 0) + (hasActionsColumn ? 1 : 0);

  return (
    <div>
      {/* Parse error banner */}
      {parseError && (
        <div role="alert" style={{ padding: 12, background: 'color-mix(in srgb, var(--danger-primary, #B3261E) 12%, var(--surface-raised))', border: '1px solid var(--danger-primary, #B3261E)', borderRadius: 'var(--radius-md)', color: 'var(--danger-primary, #B3261E)', fontSize: 13, marginBottom: 16 }}>
          <strong>YAML parse error:</strong> {parseError}
        </div>
      )}

      {entries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>
          No bookmarks yet — the tootasko-clipper extension can generate entries via its
          Save-as-DSR-Bookmark action, then paste-append them into{' '}
          <code style={{ fontFamily: 'var(--font-mono)' }}>captain-tasko/registry/bookmarks.yaml</code>.
        </div>
      ) : (
        <>
          {/* Metric cards (KPI treatment mirrors DataSourcesTable) */}
          <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
            <Kpi label="Total bookmarks" value={String(metrics.total)} />
            <Kpi label="Recent (7d)" value={String(metrics.recent)} color="var(--success-primary)" />
            <Kpi label="Unique sources" value={String(metrics.uniqueSources)} />
          </section>

          {/* Toolbar — CTSK-267's shared registry-table header. Reset
              clears search + filters + date range + sort (the Timeline
              tables' "clear everything" semantics — this table's
              default sort is the natural file order, so clearing sort
              restores the pre-CTSK-269 rendering exactly). */}
          <RegistryTableHeader
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search url, title, tags, source, note…"
            filters={[
              { label: 'All sources', value: filterSource, options: sourceOptions, onChange: setFilterSource },
              { label: 'All tags', value: filterTag, options: tagOptions, onChange: setFilterTag },
            ]}
            addEntryLabel="Add entry"
            onAddEntry={() => setShowAddHelp((v) => !v)}
            onReset={reset}
          >
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              aria-label="Captured from"
              title="Earliest capture date (UTC, inclusive)"
              style={dateInputStyle}
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              aria-label="Captured to"
              title="Latest capture date (UTC, inclusive)"
              style={dateInputStyle}
            />
          </RegistryTableHeader>

          {/* Add-entry guidance popover — CTSK-249: bookmarks are
              clipper-authored; a functional add form is deferred to a
              follow-up ticket. Guidance beats a dead button. */}
          {showAddHelp && (
            <div role="note" aria-label="How to add bookmarks" style={{ position: 'relative', padding: '10px 36px 10px 12px', marginBottom: 12, background: 'color-mix(in srgb, var(--info-primary) 10%, var(--surface-raised))', border: '1px solid var(--info-primary)', borderRadius: 'var(--radius-md)', fontSize: 13, maxWidth: 640 }}>
              To add bookmarks, use the tootasko-clipper browser extension's{' '}
              <strong>"Save as DSR Bookmark"</strong> action — it generates an entry that is
              paste-appended into{' '}
              <code style={{ fontFamily: 'var(--font-mono)' }}>registry/bookmarks.yaml</code>.
              In-app add is planned as a follow-up.
              <button
                type="button"
                onClick={() => setShowAddHelp(false)}
                aria-label="Dismiss"
                style={{ position: 'absolute', top: 6, right: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, lineHeight: 0 }}
              >
                <X size={12} />
              </button>
            </div>
          )}

          {/* Row-count summary (matches the CTSK-267 table tabs) */}
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
            {sorted.length === entries.length
              ? `${entries.length} bookmarks`
              : `Showing ${sorted.length} of ${entries.length} bookmarks`}
          </div>

          {sorted.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>
              No bookmarks match.
            </div>
          ) : (
            <table className="grid" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 280 }} />
                <col style={{ width: 220 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 180 }} />
                <col style={{ width: 180 }} />
                <col />
                <col style={{ width: 200 }} />
                {/* CTSK-364 — Card col only when a row carries a
                    captured hyper-scanner card link. */}
                {hasCardLinks && <col style={{ width: 90 }} />}
                {/* CTSK-351/364 — Actions col when rowActions declared
                    and/or the table is editable. */}
                {hasActionsColumn && <col style={{ width: 220 }} />}
              </colgroup>
              <thead>
                <tr>
                  <SortableTh sortKey="title" sort={sort} onSortChange={setSort}>Title</SortableTh>
                  <SortableTh sortKey="url" sort={sort} onSortChange={setSort}>URL</SortableTh>
                  <SortableTh sortKey="captured_at" sort={sort} onSortChange={setSort}>Captured</SortableTh>
                  <SortableTh sortKey="source" sort={sort} onSortChange={setSort}>Source</SortableTh>
                  <th>Tags</th>
                  <th>Note</th>
                  {/* CTSK-350 — ID column (was rightmost until CTSK-351 added Actions). */}
                  <SortableTh sortKey="id" sort={sort} onSortChange={setSort}>ID</SortableTh>
                  {/* CTSK-364 — hyper-scanner card links. */}
                  {hasCardLinks && <th>Card</th>}
                  {/* CTSK-351/364 — rightmost Actions col. */}
                  {hasActionsColumn && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sorted.map((b, idx) => {
                  // CTSK-264 — read-time validation: same dispatcher the
                  // append endpoint runs (CTSK-255), against the normalized
                  // entry (js-yaml Date→ISO-string coercion already applied,
                  // matching the write-time JSON shape). Invalid rows keep
                  // their cells — the operator needs to see what's there.
                  const validation = validateRegistryEntry('bookmarks-v1', b);
                  const cells = (
                    <>
                    <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={b.title}>
                      <a
                        href={b.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'var(--accent-primary)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%' }}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.title}</span>
                        <ExternalLink size={11} aria-hidden="true" style={{ flexShrink: 0 }} />
                      </a>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.id}</div>
                    </td>
                    <td
                      style={{ fontSize: 12, fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={b.url}
                    >
                      {b.url}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }} title={b.captured_at}>
                      {dateOnly(b.captured_at)}
                    </td>
                    <td style={{ fontSize: 12, fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={b.source}>
                      {b.source}
                    </td>
                    <td>
                      {b.tags && b.tags.length > 0 ? (
                        <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                          {b.tags.map((t) => (
                            <span
                              key={t}
                              style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 999, fontSize: 11, background: 'color-mix(in srgb, var(--accent-primary) 12%, var(--surface-raised))', color: 'var(--accent-primary)' }}
                            >
                              {t}
                            </span>
                          ))}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={b.note ?? ''}>
                      {b.note ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    {/* CTSK-350 — stable entry identifier, click-to-copy.
                        CSS-ellipsis truncation (not first-8 chars: every id
                        shares the `bkm_<yyyymmdd>` prefix, so a fixed 8-char
                        slice would render near-identical cells); full id in
                        the tooltip + clipboard. */}
                    {b.id ? (
                      <td
                        onClick={() => copyBookmarkId(b.id)}
                        title={`${b.id} — click to copy`}
                        style={{ fontSize: 12, fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                      >
                        {b.id}
                      </td>
                    ) : (
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</td>
                    )}
                    {/* CTSK-364 — hyper-scanner card link (fetch-and-map
                        capture). Rows without a captured card show an
                        empty cell; the column itself only exists when
                        at least one row has one. */}
                    {hasCardLinks && (
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {typeof b.external_refs?.hyper_scan?.card_url === 'string'
                          && (b.external_refs.hyper_scan.card_url as string).length > 0 ? (
                          <a
                            href={b.external_refs.hyper_scan.card_url as string}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={`Open the hyper-scanner card for ${b.title}`}
                            style={{ color: 'var(--accent-primary)', fontSize: 12, textDecoration: 'none' }}
                          >
                            🔍 Card
                          </a>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                        )}
                      </td>
                    )}
                    {/* CTSK-351 — per-row action palette. The palette
                        component filters out actions whose requiresField
                        is missing on the row and returns null when
                        nothing renders — so a row missing every action's
                        required field shows an empty cell rather than
                        broken buttons. CTSK-364 adds the Edit affordance
                        (mutable tables) + fetch-and-map wiring. */}
                    {hasActionsColumn && (
                      <td style={{ overflow: 'hidden' }}>
                        <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                          {hasRowActions && (
                            <RegistryRowActionsPalette
                              actions={rowActions}
                              entry={b as unknown as Record<string, unknown>}
                              interpolationCtx={mutation?.makeEntryRef && b.id
                                ? { captain_tasko_ref: mutation.makeEntryRef(b.id) }
                                : undefined}
                              onPatchEntry={mutation
                                ? (entryId, patch) => patchRegistryEntry(mutation.toolId, mutation.filePath, entryId, patch)
                                : undefined}
                              onRefreshRequested={mutation?.onRefetch}
                            />
                          )}
                          {editable && b.id && (
                            <button
                              type="button"
                              onClick={() => setEditingId((cur) => (cur === b.id ? null : b.id))}
                              title={`Edit bookmark ${b.id} inline`}
                              aria-expanded={editingId === b.id}
                              style={editActionBtnStyle}
                            >
                              <Pencil size={12} aria-hidden="true" />
                              <span>Edit</span>
                            </button>
                          )}
                        </span>
                      </td>
                    )}
                    </>
                  );
                  const key = b.id ?? `row-${idx}`;
                  const row = validation.ok
                    ? <tr key={key}>{cells}</tr>
                    : <WarningRow key={key} issues={validation.errors}>{cells}</WarningRow>;
                  // CTSK-364 (CTSK-353 fold) — inline editor expansion
                  // row directly under the row being edited. Save
                  // refetches the YAML (persisted bytes are the source
                  // of truth); cancel just collapses.
                  if (editable && mutation && editingId === b.id) {
                    return (
                      <React.Fragment key={`${key}-editing`}>
                        {row}
                        <tr>
                          <td colSpan={columnCount} style={{ padding: '4px 0 10px' }}>
                            <BookmarkRowEditor
                              bookmark={b}
                              toolId={mutation.toolId}
                              filePath={mutation.filePath}
                              onSaved={() => {
                                setEditingId(null);
                                mutation.onRefetch();
                              }}
                              onCancel={() => setEditingId(null)}
                            />
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  }
                  return row;
                })}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────

// KPI card — same visual treatment as DataSourcesTable's private Kpi
// (replicated rather than exported from there: CTSK-269 leaves the
// Data Sources tab untouched; if a third consumer appears, extract to
// a shared component alongside RegistryTableHeader).
function Kpi({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '12px 14px' }}>
      <div style={{ color: 'var(--text-muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4, color: color ?? 'var(--text-primary)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// CTSK-364 — Edit chip, visually matched to the RegistryRowActionsPalette
// button treatment so the Actions cell reads as one strip.
const editActionBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 8px',
  fontSize: 11,
  fontWeight: 500,
  fontFamily: 'inherit',
  color: 'var(--accent-primary)',
  background: 'color-mix(in srgb, var(--accent-primary) 8%, var(--surface-raised))',
  border: '1px solid color-mix(in srgb, var(--accent-primary) 35%, var(--border-subtle))',
  borderRadius: 'var(--radius-sm, 4px)',
  cursor: 'pointer',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
};

const dateInputStyle: React.CSSProperties = {
  padding: '5px 8px',
  fontSize: 12,
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  background: 'var(--surface-raised)',
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
};

// ── Helpers ────────────────────────────────────────────────────────────

// js-yaml resolves ISO-shaped scalars (captured_at) to JS Date
// objects per the YAML 1.1 timestamp tag; rendering a Date into JSX
// throws. Normalize to ISO strings up-front. Note text from `>`
// folded blocks carries a trailing newline — trim it.
function normalizeBookmark(b: Bookmark): Bookmark {
  const out = { ...b };
  const cap = out.captured_at as unknown;
  if (cap instanceof Date) out.captured_at = cap.toISOString();
  if (typeof out.note === 'string') out.note = out.note.trim();
  return out;
}

/** '2026-07-29T15:32:00Z' → '2026-07-29' (date-only per spec). */
function dateOnly(iso: string): string {
  if (typeof iso !== 'string') return String(iso);
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1]! : iso;
}
